import React, { useCallback, useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import jsQR from "jsqr";
import { X, ShieldCheck, ShieldAlert, Camera, QrCode, Loader2, CheckCircle2 } from "lucide-react";
import { devDebug } from "../lib/devLog";
import {
  buildSafetyQrPayload,
  parseSafetyQrPayload,
  markPeerVerified,
  isPeerVerified,
  unmarkPeerVerified,
} from "../lib/dmVerified";

/**
 * Out-of-band DM key verification à la Signal.
 *
 * Two tabs:
 *   • "Il mio QR"  → shows a QR the peer can scan; the QR encodes the two
 *                    x25519 pubkeys (sorted) + the safety number so the
 *                    scanner can recompute and compare locally.
 *   • "Scansiona"  → opens the camera (or accepts a photo file) and
 *                    matches the payload against locally-computed values.
 *
 * A successful match writes "verified" state to localStore for that peer.
 */
export function SafetyNumberDialog({
  peer,
  myX25519Hex,
  peerX25519Hex,
  safetyNumber,
  verified,
  onClose,
  onVerifiedChange,
}) {
  const [tab, setTab] = useState("show");
  const [manualVerified, setManualVerified] = useState(() => isPeerVerified(peer?.identityId));
  const canVerify = verified === true && !!safetyNumber && !!myX25519Hex && !!peerX25519Hex;

  const notify = useCallback((next) => {
    setManualVerified(next);
    onVerifiedChange?.(next);
  }, [onVerifiedChange]);

  // Escape-to-close for keyboard/screen-reader users.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="safety-number-modal"
      role="presentation"
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-md relative overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Numero di sicurezza"
      >
        <div className="px-5 pt-5 pb-3 flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
            <ShieldCheck size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display font-semibold text-lg text-ink leading-tight">
              Numero di sicurezza
            </div>
            <div className="text-[11px] text-muted mt-0.5">
              Confronta questo numero con {peer?.displayName || "il contatto"} tramite un canale
              alternativo (voce, di persona, video). Se coincide, nessuno sta intercettando la
              conversazione.
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full hover:bg-cream flex items-center justify-center shrink-0"
            data-testid="safety-number-close"
            aria-label="Chiudi"
          >
            <X size={16} />
          </button>
        </div>

        {canVerify ? (
          <>
            <div className="px-5">
              <div className="font-mono text-center text-sm tracking-widest bg-cream rounded-2xl py-3 px-2 text-ink select-all" data-testid="safety-number-value">
                {safetyNumber || "—"}
              </div>
            </div>

            <div className="mt-4 mx-5 border border-edge rounded-full p-1 flex text-xs font-semibold">
              <button
                onClick={() => setTab("show")}
                className={`flex-1 py-1.5 rounded-full flex items-center justify-center gap-1.5 transition ${
                  tab === "show" ? "bg-ink text-white" : "text-muted hover:text-ink"
                }`}
                data-testid="safety-number-tab-show"
              >
                <QrCode size={13} /> Il mio QR
              </button>
              <button
                onClick={() => setTab("scan")}
                className={`flex-1 py-1.5 rounded-full flex items-center justify-center gap-1.5 transition ${
                  tab === "scan" ? "bg-ink text-white" : "text-muted hover:text-ink"
                }`}
                data-testid="safety-number-tab-scan"
              >
                <Camera size={13} /> Scansiona
              </button>
            </div>

            <div className="p-5">
              {tab === "show" ? (
                <ShowQrPane
                  myX25519Hex={myX25519Hex}
                  peerX25519Hex={peerX25519Hex}
                  safetyNumber={safetyNumber}
                />
              ) : (
                <ScanQrPane
                  expectedSafety={safetyNumber}
                  myX25519Hex={myX25519Hex}
                  peerX25519Hex={peerX25519Hex}
                  onMatch={() => {
                    markPeerVerified(peer?.identityId);
                    notify(true);
                  }}
                />
              )}
            </div>

            <div className="border-t border-edge px-5 py-3 flex items-center gap-2 bg-cream/40">
              {manualVerified ? (
                <>
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700" data-testid="safety-number-verified-badge">
                    <ShieldCheck size={14} /> Verificato di persona
                  </span>
                  <button
                    onClick={() => { unmarkPeerVerified(peer?.identityId); notify(false); }}
                    className="ml-auto text-xs text-muted hover:text-primary underline"
                    data-testid="safety-number-unmark"
                  >
                    Rimuovi verifica
                  </button>
                </>
              ) : (
                <>
                  <span className="inline-flex items-center gap-1.5 text-xs text-amber-700">
                    <ShieldAlert size={14} /> Non ancora verificato
                  </span>
                  <button
                    onClick={() => { markPeerVerified(peer?.identityId); notify(true); }}
                    className="ml-auto text-xs font-semibold text-emerald-700 hover:text-emerald-800 underline"
                    data-testid="safety-number-mark-manual"
                    title="Conferma manualmente dopo aver confrontato il numero su un canale alternativo"
                  >
                    Ho confrontato il numero
                  </button>
                </>
              )}
            </div>
          </>
        ) : (
          <div className="p-5" data-testid="safety-number-unverified-info">
            <div className="rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-xs text-amber-900 leading-relaxed">
              <div className="font-semibold text-sm text-amber-800 flex items-center gap-1.5 mb-1">
                <ShieldAlert size={14} /> Chiave DM non ancora firmata
              </div>
              {peer?.displayName || "Il contatto"} non ha (ancora) pubblicato una firma
              crittografica della sua chiave DM (SEC-001). Il messaggio è comunque cifrato E2E,
              ma senza binding un server compromesso potrebbe teoricamente sostituire la chiave.
              <br /><br />
              Il numero di sicurezza sarà disponibile appena il contatto aprirà l'app aggiornata:
              la firma viene generata automaticamente al primo login.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ShowQrPane({ myX25519Hex, peerX25519Hex, safetyNumber }) {
  const canvasRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!canvasRef.current || !myX25519Hex || !peerX25519Hex || !safetyNumber) return;
    const payload = buildSafetyQrPayload({ myX25519Hex, peerX25519Hex, safetyNumber });
    QRCode.toCanvas(canvasRef.current, payload, {
      width: 240,
      margin: 1,
      color: { dark: "#111111", light: "#ffffff" },
      errorCorrectionLevel: "M",
    }).catch((err) => {
      devDebug("QRCode.toCanvas failed", err?.message);
      setError("Impossibile generare il QR code");
    });
  }, [myX25519Hex, peerX25519Hex, safetyNumber]);

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="rounded-2xl border border-edge p-3 bg-white">
        <canvas ref={canvasRef} data-testid="safety-number-qr-canvas" aria-label="QR code numero di sicurezza" />
      </div>
      <div className="text-[11px] text-muted text-center max-w-xs">
        Chiedi al contatto di aprire questa stessa finestra e toccare "Scansiona" per confrontare
        le chiavi.
      </div>
      {error && <div className="text-xs text-primary">{error}</div>}
    </div>
  );
}

function ScanQrPane({ expectedSafety, myX25519Hex, peerX25519Hex, onMatch }) {
  const videoRef = useRef(null);
  const rafRef = useRef(0);
  const streamRef = useRef(null);
  const cancelledRef = useRef(false);
  const scanCanvasRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | running | matched | mismatch | error
  const [error, setError] = useState(null);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
    if (videoRef.current) {
      try { videoRef.current.srcObject = null; } catch (err) {
        devDebug("SafetyNumberDialog: could not clear srcObject", err?.message);
      }
    }
  }, []);

  // Guard both against normal unmount AND against getUserMedia resolving
  // after the component was already gone (MEDIUM in code review): the
  // cancelled flag lets startCamera abort right after the await.
  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
      stopStream();
    };
  }, [stopStream]);

  const checkPayload = useCallback((text) => {
    const parsed = parseSafetyQrPayload(text);
    if (!parsed) { setStatus("mismatch"); setError("QR non riconosciuto"); return false; }
    // Recompute the sorted pair locally and compare with the scanned one.
    const my = String(myX25519Hex || "").toLowerCase();
    const peer = String(peerX25519Hex || "").toLowerCase();
    const [lo, hi] = my < peer ? [my, peer] : [peer, my];
    const pairOk = parsed.a === lo && parsed.b === hi;
    const snOk = parsed.safetyNumber === expectedSafety;
    if (pairOk && snOk) {
      setStatus("matched");
      onMatch?.();
      stopStream();
      return true;
    }
    setStatus("mismatch");
    setError(pairOk ? "Il numero non coincide con quello calcolato localmente" : "Le chiavi X25519 non coincidono — possibile MITM");
    return false;
  }, [expectedSafety, myX25519Hex, peerX25519Hex, onMatch, stopStream]);

  const scanLoop = useCallback(() => {
    if (cancelledRef.current) return;
    const video = videoRef.current;
    if (!video || video.readyState !== video.HAVE_ENOUGH_DATA) {
      rafRef.current = requestAnimationFrame(scanLoop);
      return;
    }
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) {
      rafRef.current = requestAnimationFrame(scanLoop);
      return;
    }
    // Reuse a single canvas across frames to avoid per-frame GC churn.
    if (!scanCanvasRef.current) {
      scanCanvasRef.current = document.createElement("canvas");
    }
    const canvas = scanCanvasRef.current;
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(video, 0, 0, w, h);
    const imgData = ctx.getImageData(0, 0, w, h);
    const code = jsQR(imgData.data, w, h, { inversionAttempts: "attemptBoth" });
    if (code && code.data) {
      const done = checkPayload(code.data);
      if (done) return;
    }
    rafRef.current = requestAnimationFrame(scanLoop);
  }, [checkPayload]);

  const startCamera = useCallback(async () => {
    setError(null);
    setStatus("running");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      // If the user closed the dialog while the OS permission prompt was
      // pending, tear the stream down instead of leaking the camera light.
      if (cancelledRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      rafRef.current = requestAnimationFrame(scanLoop);
    } catch (err) {
      devDebug("getUserMedia failed", err?.message);
      setStatus("error");
      setError(err?.name === "NotAllowedError"
        ? "Permesso fotocamera negato. Consenti l'accesso o carica una foto del QR."
        : "Impossibile accedere alla fotocamera. Puoi caricare una foto del QR.");
    }
  }, [scanLoop]);

  const onFile = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    try {
      const bmp = await createImageBitmap(file);
      const canvas = document.createElement("canvas");
      canvas.width = bmp.width; canvas.height = bmp.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(bmp, 0, 0);
      const img = ctx.getImageData(0, 0, bmp.width, bmp.height);
      const code = jsQR(img.data, bmp.width, bmp.height, { inversionAttempts: "attemptBoth" });
      if (!code || !code.data) { setStatus("mismatch"); setError("Nessun QR trovato nell'immagine"); return; }
      checkPayload(code.data);
    } catch (err) {
      devDebug("QR file decode failed", err?.message);
      setStatus("error");
      setError("Impossibile decodificare l'immagine");
    }
  }, [checkPayload]);

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative rounded-2xl overflow-hidden border border-edge bg-black w-full aspect-square max-w-[240px] flex items-center justify-center">
        <video ref={videoRef} playsInline muted className="w-full h-full object-cover" data-testid="safety-number-video" />
        {status === "idle" && (
          <div className="absolute inset-0 flex items-center justify-center text-white text-xs bg-black/60">
            Fotocamera non attiva
          </div>
        )}
        {status === "running" && (
          <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full">
            <Loader2 size={10} className="animate-spin" /> Scansiono…
          </div>
        )}
        {status === "matched" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-emerald-600/90 text-white text-sm gap-1" data-testid="safety-number-match">
            <CheckCircle2 size={28} />
            <div className="font-semibold">Numero verificato</div>
          </div>
        )}
      </div>

      {status !== "matched" && (
        <div className="flex flex-col sm:flex-row items-stretch gap-2 w-full">
          <button
            onClick={startCamera}
            className="flex-1 py-2 rounded-full bg-ink text-white text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-black"
            data-testid="safety-number-start-camera"
          >
            <Camera size={13} /> Apri fotocamera
          </button>
          <label className="flex-1 py-2 rounded-full border border-edge text-ink text-xs font-semibold flex items-center justify-center gap-1.5 hover:border-ink cursor-pointer">
            <QrCode size={13} /> Carica foto
            <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="safety-number-file-input" />
          </label>
        </div>
      )}

      {error && (
        <div className="text-xs text-primary text-center" data-testid="safety-number-scan-error">{error}</div>
      )}
      {status === "matched" && (
        <div className="text-xs text-emerald-700 text-center">
          Le chiavi coincidono. Questa conversazione è ora contrassegnata come verificata sul tuo
          dispositivo.
        </div>
      )}
    </div>
  );
}
